import runtime from '../runtime';

export const isSupported = () => (
    runtime.isWeb() && typeof navigator !== 'undefined' && 'serviceWorker' in navigator
);

export const getCurrentAppVersion = () => import.meta.env.VITE_VERSION || '1.0.0';

export const checkForAppUpdate = async () => {
    if (!runtime.isWeb()) return false;
    try {
        const response = await fetch('/api/version', {
            cache: 'no-cache',
            headers: { 'Cache-Control': 'no-cache' }
        });
        if (!response.ok) return false;
        const { version } = await response.json();
        return version !== getCurrentAppVersion();
    } catch (error) {
        console.error('Error checking for app update:', error);
        return false;
    }
};

export const clearAllCaches = async () => {
    if (typeof window === 'undefined' || !('caches' in window)) return;
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
};

export const reload = () => {
    if (typeof window !== 'undefined') window.location.reload();
};

export const getRegistration = async (scope = '/sw.js') => {
    if (!isSupported()) return null;
    return navigator.serviceWorker.getRegistration(scope);
};

export const register = async (script = '/sw.js', options = {}) => {
    if (!isSupported()) return null;
    return navigator.serviceWorker.register(script, options);
};

export const updateServiceWorker = async () => {
    const registration = await getRegistration();
    if (!registration) return false;
    await registration.update();
    return true;
};

export const isDocumentVisible = () => (
    typeof document !== 'undefined' && document.visibilityState === 'visible'
);

export const hasController = () => (
    isSupported() && navigator.serviceWorker.controller !== null
);

export const onWindowFocus = (callback) => {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener('focus', callback);
    return () => window.removeEventListener('focus', callback);
};

const onServiceWorkerEvent = (eventName, callback) => {
    if (!isSupported()) return () => {};
    navigator.serviceWorker.addEventListener(eventName, callback);
    return () => navigator.serviceWorker.removeEventListener(eventName, callback);
};

export const onMessage = (callback) => onServiceWorkerEvent('message', callback);
export const onControllerChange = (callback) => onServiceWorkerEvent('controllerchange', callback);

export default Object.freeze({
    isSupported,
    getCurrentAppVersion,
    checkForAppUpdate,
    clearAllCaches,
    reload,
    getRegistration,
    register,
    updateServiceWorker,
    isDocumentVisible,
    hasController,
    onWindowFocus,
    onMessage,
    onControllerChange
});
